import { orderBy, sortBy, update } from 'lodash';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IPerformLog, PerformLogBackend, PerformLogEvidence, PerformLogForm } from './perform-log.type';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { ErrorHandlingService } from 'src/app/services/error-handling.service';
import { catchError, map, tap } from 'rxjs/operators';
import { DataCacher } from 'src/app/shared/utils/class/DataCacher';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { ListCacher } from 'src/app/shared/utils/class/ListCacher';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from 'src/app/services/user.service';
import { AddEditPerformLogModalComponent } from './add-edit-perform-log-modal/add-edit-perform-log-modal.component';
import { EvidencePayloadType, EvidenceType } from 'src/app/services/evidence.service';
import { DeleteModalComponent } from 'src/app/shared/popups/learning-record-actions/delete-modal/delete-modal.component';
import { ConfirmationModal } from 'src/app/shared/utils/base-class/confirmation-modal';
import { PerformLogDeleteModalComponent } from './perform-log-delete-modal/perform-log-delete-modal.component';
import { filter } from 'underscore';
import { HierarchyService } from '../../admin/hierarchy/hierarchy.service';
import { GroupService } from 'src/app/services/group.service';

@Injectable({
  providedIn: 'root'
})
export class PerformLogService {
  private modalService = inject(NgbModal);
  private userService = inject(UserService);
  private hierarchyService = inject(HierarchyService);
  private httpClient = inject(HttpClient);
  private groupService = inject(GroupService);
  private errorHandlingService = inject(ErrorHandlingService);
  private performLogsCacher = new ListCacher<IPerformLog>(this.fetchAllPerformLogs());

  constructor() { }

  fetchAllPerformLogs(): Observable<IPerformLog[]> {
    let url;
    if(this.userService.isManager){
      url = `${environment.accountServiceEndpoint}/orgs/${localStorage.getItem('orgID')}/perfReviewLogs`; // OM can view all orgs
    } else {
      url = `${environment.accountServiceEndpoint}/orgs/${localStorage.getItem('orgID')}/perfReviewLogs/manager/managed`; // DM only view the
    }
    return this.httpClient.get<PerformLogBackend[]>(url).pipe(
      catchError(this.errorHandlingService.handleHttpError),
      map((logs) => (
        this.extendPerformLogBackend(logs)
      ))
    );
  }

  extendPerformLogBackend(logs: PerformLogBackend[]): IPerformLog[]{
    return orderBy(logs.map(log => ({
      ...log,
      createdByOrgUser: this.userService.managedOrgUserDictionaryByUserID[log.createdBy],
      staffOrgUser: this.userService.managedOrgUserDictionaryByUserID[log.staffUserID],
      managerOrgUser: this.hierarchyService.getDirectManagerByOrgUserID(this.userService.managedOrgUserDictionaryByUserID[log.staffUserID]?.orgUserID),
      fn_relatedStandards: log.standards,
      fn_relatedOrgStandards: log.orgStandards,
    })).map(
      log => ({
        ...log.staffOrgUser,
        user: log.staffOrgUser,
        taskManager: log.managerOrgUser,
        completeDate: log.createDate,
        evidences: this.convertBackendNotesToFnNotes(log.notes),
        teams: log.staffOrgUser?.teams?.map(t => this.groupService.globalTeamsDict?.[t]),
        jobRoles: log.staffOrgUser?.jobRoleTakens?.map(j => j.jobRoleModel),
        ...log,
      } as any)
    ), ['createDate'], ['desc'])
  }

  fetchCachedAllPerformLogs(refresh = false): Observable<IPerformLog[]> {
    return this.performLogsCacher.fetchCachedData(refresh);
  }

  fetchCachedPerformLogsByOrgUserID(orgUserID: string){
    return this.fetchCachedAllPerformLogs().pipe(
      map((logs) => {
        return logs.filter(log => log.staffOrgUser?.orgUserID === orgUserID);
      })
    )
  }

  performLogModal(editedLog?: IPerformLog) {
    const modal = this.modalService.open(AddEditPerformLogModalComponent, { size: 'lg' });
    if (editedLog) {
      (modal.componentInstance as AddEditPerformLogModalComponent).editedLog.set(editedLog);
    }
  }

  performLogDeleteModal(deletedLog: IPerformLog){
    const modal = this.modalService.open(PerformLogDeleteModalComponent, { size: 'lg' });
    modal.componentInstance.logID.set(deletedLog.logID);
  }

  createPerformLog(payload: PerformLogForm){
    const url = `${environment.accountServiceEndpoint}/orgs/${localStorage.getItem('orgID')}/perfReviewLogs`;
    return this.httpClient.post<PerformLogBackend>(url, payload).pipe(
      catchError(this.errorHandlingService.handleHttpError),
    );
  }

  updatePerformLog(logID: string, payload: PerformLogForm){
    const url = `${environment.accountServiceEndpoint}/orgs/${localStorage.getItem('orgID')}/perfReviewLogs/${logID}`;
    return this.httpClient.put<PerformLogBackend>(url, payload).pipe(
      catchError(this.errorHandlingService.handleHttpError),
    );
  }

  deletePerformLog(logID: string){
    const url = `${environment.accountServiceEndpoint}/orgs/${localStorage.getItem('orgID')}/perfReviewLogs/${logID}`;
    return this.httpClient.delete(url).pipe(
      catchError(this.errorHandlingService.handleHttpError),
      tap(() => {
        this.performLogsCacher.deleteEl(logID, 'logID');
      }),
    );
  }

  createPerformLogNote(logID: string, notes: EvidencePayloadType){
    const url = `${environment.accountServiceEndpoint}/orgs/${localStorage.getItem('orgID')}/perfReviewLogs/${logID}/notes`;
    return this.httpClient.post<PerformLogBackend>(url, notes).pipe(
      catchError(this.errorHandlingService.handleHttpError),
      // map((logs) => (
      //   this.extendPerformLogBackend(logs)
      // ))
    );
  }

  deletePerformLogNote(logID: string, noteID: string){
    const url = `${environment.accountServiceEndpoint}/orgs/${localStorage.getItem('orgID')}/perfReviewLogs/${logID}/notes/${noteID}`;
    return this.httpClient.delete(url).pipe(
      catchError(this.errorHandlingService.handleHttpError),
    );
  }

  addNewLogIntoCacher(log: PerformLogBackend){
    this.performLogsCacher.pushNew(this.extendPerformLogBackend([log]));
  }

  updateLogInCacher(log: PerformLogBackend){
    this.performLogsCacher.updateEl(this.extendPerformLogBackend([log])[0], 'logID');
  }

  convertBackendNotesToFnNotes(notes: EvidenceType[]): PerformLogEvidence[]{
    return notes?.map(note => ({
      noteID: note?.noteID,
      title: note?.links?.[0]?.title,
      link: note?.links?.[0]?.link,
      description: note?.note,
      managerUserID: note?.addedBy,
      createTime: note?.addedDate,
    }));
  }
}

